import React, { forwardRef } from "react";
import "../css/templates.css";

const ResumeDocument = forwardRef(
  ({ formData, design, style, className = "" }, ref) => {
    const sectionOrder = design.sectionOrder || [
      "personal",
      "summary",
      "experience",
      "education",
      "projects",
      "skills",
    ];

    return (
      <div
        ref={ref}
        className={`a4-document ${design.theme} font-${design.font
          .replace(/\s+/g, "-")
          .toLowerCase()} ${className}`}
        style={{
          "--accent-color": design.color,
          "--text-color": design.textColor,
          "--bg-color": design.backgroundColor,
          backgroundColor: "var(--bg-color)",
          color: "var(--text-color)",
          ...style,
        }}
      >
        <header className="res-header">
          {/* Profile Picture Section */}
          {/* Conditionally remove from DOM for the Editorial theme */}
          {design.theme !== "editorial" && formData.personal.picture && (
            <div className="res-profile-pic-container">
              <img
                src={formData.personal.picture}
                alt="Profile"
                className="res-profile-pic"
              />
            </div>
          )}

          {/* Text Content Section */}
          <div className="res-header-text">
            <h1>{formData.personal.name || "Your Name"}</h1>
            {formData.personal.role && (
              <p className="res-role">{formData.personal.role}</p>
            )}
            <div className="res-contact">
              {/* Conditionally render spans to prevent floating CSS dots */}
              {(formData.personal.email || !formData.personal.name) && (
                <span>{formData.personal.email || "Email"}</span>
              )}
              {(formData.personal.phone || !formData.personal.name) && (
                <span>{formData.personal.phone || "Phone"}</span>
              )}
              {(formData.personal.location || !formData.personal.name) && (
                <span>{formData.personal.location || "Location"}</span>
              )}

              {formData.personal.customLinks &&
                formData.personal.customLinks.length > 0 &&
                formData.personal.customLinks.map(
                  (link, index) =>
                    link.url && (
                      <span key={index} className="res-custom-link">
                        <a
                          href={
                            link.url.startsWith("http")
                              ? link.url
                              : `https://${link.url}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          {link.label || link.url.replace(/^https?:\/\//, "")}
                        </a>
                      </span>
                    ),
                )}
            </div>
          </div>
        </header>

        <div className="res-body">
          {sectionOrder.map((sectionId) => {
            switch (sectionId) {
              case "summary":
                if (!formData.summary || formData.summary.trim() === "")
                  return null;
                return (
                  <section key={sectionId} className="res-section">
                    <h3 className="res-section-title">Summary</h3>
                    <p className="res-text">{formData.summary}</p>
                  </section>
                );

              case "experience":
                if (!formData.experience || formData.experience.length === 0)
                  return null;
                return (
                  <section key={sectionId} className="res-section">
                    <h3 className="res-section-title">Experience</h3>
                    <div className="res-timeline">
                      {formData.experience.map((exp) => (
                        <div key={exp.id} className="res-job">
                          <div className="job-header">
                            <span className="job-title">
                              {exp.role || "Job Title"}
                              {/* Removed the hardcoded "@" symbol so CSS can apply custom separators */}
                              {exp.company && (
                                <span className="job-company">
                                  {exp.company}
                                </span>
                              )}
                            </span>
                            <span className="job-date">
                              {exp.dates || "Dates"}
                            </span>
                          </div>
                          <ul className="res-list">
                            {(Array.isArray(exp.desc) ? exp.desc : [exp.desc])
                              .filter(
                                (b) => typeof b === "string" && b.trim() !== "",
                              )
                              .map((bullet, i) => (
                                <li key={i}>{bullet}</li>
                              ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>
                );

              case "education":
                if (!formData.education || formData.education.length === 0)
                  return null;
                return (
                  <section key={sectionId} className="res-section">
                    <h3 className="res-section-title">Education</h3>
                    <div className="res-timeline">
                      {formData.education.map((ed) => (
                        <div key={ed.id} className="res-job">
                          <div className="job-header">
                            <span className="job-title">
                              {ed.degree || "Degree"}
                              {ed.school && (
                                <span className="job-company">{ed.school}</span>
                              )}
                            </span>
                            <span className="job-date">
                              {ed.year || "Year"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );

              case "projects":
                if (!formData.projects || formData.projects.length === 0)
                  return null;
                return (
                  <section key={sectionId} className="res-section">
                    <h3 className="res-section-title">Projects</h3>
                    <div className="res-timeline">
                      {formData.projects.map((pr) => (
                        <div key={pr.id} className="res-job">
                          <div className="job-header">
                            <span className="job-title">
                              {pr.title || "Project Title"}
                              {pr.link && (
                                <span className="job-company">
                                  <a
                                    href={
                                      pr.link.startsWith("http")
                                        ? pr.link
                                        : `https://${pr.link}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "inherit",
                                      textDecoration: "none",
                                    }}
                                  >
                                    {pr.link.replace(/^https?:\/\//, "")}
                                  </a>
                                </span>
                              )}
                            </span>
                          </div>
                          {pr.desc && <p className="res-text">{pr.desc}</p>}
                        </div>
                      ))}
                    </div>
                  </section>
                );

              case "skills":
                if (
                  !formData.skills ||
                  formData.skills.filter((s) => s.trim() !== "").length === 0
                )
                  return null;

                return (
                  <section key={sectionId} className="res-section">
                    <h3 className="res-section-title">Skills</h3>
                    <div className="res-skills">
                      {formData.skills
                        .filter((s) => s.trim() !== "")
                        .map((skill, i) => (
                          <span key={i} className="res-skill-badge">
                            {skill.trim()}
                          </span>
                        ))}
                    </div>
                  </section>
                );

              default:
                return null;
            }
          })}
        </div>
      </div>
    );
  },
);

export default ResumeDocument;
